/**
 * Claiming an account an AI agent signed up (migration 080).
 *
 * An agent with no account can create one itself (`/partner/signup/*`): it
 * proves the website, names a person authorised to bind the organisation, and
 * accepts the Partner Terms for it on that person's authority. The
 * organisation works fully from then on, but stays UNCLAIMED until that person
 * signs in with the named email and confirms here. Unclaimed for 14 days, its
 * widget keys stop until they do.
 *
 *   - `ClaimBanner`: on every signed-in page while `whoami.claim_pending` is
 *     set. Not dismissible: it is the one thing the person has to do.
 *   - `Claim` (`/claim`): what the agent set up, the Terms, a required
 *     checkbox, and "Confirm and take ownership" → `POST /partner/org/claim`,
 *     which records this person's own acceptance and turns the keys back on.
 */
import { useId, useState } from 'react';
import type { ClaimState } from '../api/types';
import { useSession } from '../auth/session';
import { CURRENT_TERMS, TERMS_PATH, legalVersionLabel } from '../legal/current';
import { Link } from '../router';
import { useMutation, useQuery } from '../ui/hooks';
import { Empty, ErrorNotice, Loading, PageHead, formatDateTime } from '../ui/kit';

export const CLAIM_HEADLINE = 'Your AI agent set this up — confirm to take ownership';

function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Why the keys are off, in the words a partner reads. */
export function keysOffSentence(reason: string | null | undefined): string {
  return reason === 'contact_reported'
    ? 'The widget keys are switched off because the named person reported that they did not authorise this account. FinderlyFix is looking into it.'
    : 'Nobody claimed this account within 14 days, so its widget keys are switched off until it is claimed. Nothing was deleted.';
}

export function agentName(c: Pick<ClaimState, 'agent_client'>): string {
  return [c.agent_client.name, c.agent_client.version].filter(Boolean).join(' ') || 'An AI agent';
}

/** The website's host, for "set up for <host>". */
export function claimHost(c: Pick<ClaimState, 'origin'>): string {
  try {
    return c.origin ? new URL(c.origin).host : 'your website';
  } catch {
    return 'your website';
  }
}

/** Said plainly, on every claim surface: an AI agent did this, not FinderlyFix. */
export function setUpBySentence(c: Pick<ClaimState, 'agent_client' | 'origin'>): string {
  return `This account was set up by an AI agent (${agentName(c)}) for ${claimHost(c)}, not by FinderlyFix.`;
}

export function ClaimBanner() {
  const { whoami } = useSession();
  const claim = whoami?.claim_pending;
  if (!claim) return null;
  const headingId = 'claim-banner-title';
  return (
    <section className={`notice ${claim.keys_disabled ? 'bad' : 'warn'} claim-banner`} aria-labelledby={headingId} data-testid="claim-banner">
      <div className="stack-sm">
        <strong id={headingId}>{CLAIM_HEADLINE}</strong>
        <p style={{ margin: 0 }}>{setUpBySentence(claim)}</p>
        <p style={{ margin: 0 }}>
          {agentName(claim)} created this account{claim.origin ? ` for ${claim.origin}` : ''} and accepted the Partner Terms for it on{' '}
          {claim.contact_email ? `${claim.contact_email}’s` : 'the named person’s'} authority.{' '}
          {claim.keys_disabled
            ? keysOffSentence(claim.keys_disabled_reason)
            : `Everything works meanwhile. If nobody claims it by ${formatDay(claim.disable_at)}, the widget keys stop working until it is claimed.`}
        </p>
        {claim.you_are_contact ? (
          <p style={{ margin: 0 }}>
            <Link className="btn btn-primary" to="/claim">
              Review and confirm
            </Link>
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            Only {claim.contact_email ?? 'the person the agent named'} can claim it: they sign in with that email and confirm.
          </p>
        )}
      </div>
    </section>
  );
}

function Facts({ claim }: { claim: ClaimState }) {
  return (
    <dl className="facts" data-testid="claim-facts">
      <dt>Set up by</dt>
      <dd>{agentName(claim)}</dd>
      <dt>Website</dt>
      <dd>{claim.origin ?? '—'}</dd>
      <dt>Proof of the website</dt>
      <dd>{claim.proof_method === 'dns' ? 'A DNS TXT record' : claim.proof_method === 'file' ? 'A verification file on the site' : '—'}</dd>
      <dt>Authorised by</dt>
      <dd>
        {[claim.attested_by?.name, claim.attested_by?.role].filter(Boolean).join(', ') || '—'}
        {claim.contact_email ? ` (${claim.contact_email})` : ''}
      </dd>
      <dt>Created</dt>
      <dd>{formatDateTime(claim.created_at)}</dd>
      <dt>Widget keys</dt>
      <dd>{claim.keys_disabled ? keysOffSentence(claim.keys_disabled_reason) : `Working. They stop on ${formatDay(claim.disable_at)} if nobody claims the account.`}</dd>
    </dl>
  );
}

export function Claim() {
  const { api, org, refreshWhoami } = useSession();
  const info = useQuery(() => api.getClaim(), [org?.id]);
  const [ticked, setTicked] = useState(false);
  const [done, setDone] = useState<{ reenabled: boolean } | null>(null);
  const checkboxId = useId();
  const confirm = useMutation(() => api.claimOrg(CURRENT_TERMS.terms_version));
  const orgName = org?.display_name ?? 'your organisation';

  const head = <PageHead title="Take ownership" lede="An AI agent set up this FinderlyFix account. Confirm it is yours." />;
  if (info.loading) return <>{head}<Loading /></>;
  if (info.error) return <>{head}<ErrorNotice error={info.error} /></>;
  const claim = info.data?.claim ?? null;
  if (!info.data?.agent_created || !claim) {
    return (
      <>
        {head}
        <Empty level={2} title="Nothing to claim">
          {orgName} wasn’t set up by an AI agent, so there is nothing to confirm.
        </Empty>
      </>
    );
  }
  if (done || claim.claimed) {
    return (
      <>
        {head}
        <div className="notice ok" role="status">
          <span>
            <strong>{orgName} is yours.</strong> Your acceptance of the Partner Terms is recorded
            {done?.reenabled ? ', and the widget keys work again.' : '.'}{' '}
            {claim.keys_disabled_reason === 'contact_reported'
              ? 'The widget keys stay off until FinderlyFix has looked into the report made from the “This wasn’t us” link; contact support@finderlyfix.com. '
              : ''}
            <Link to="/">Go to the portal</Link>
          </span>
        </div>
      </>
    );
  }
  if (!claim.you_are_contact) {
    return (
      <>
        {head}
        <Facts claim={claim} />
        <div className="notice info">
          <span>Only {claim.contact_email ?? 'the person the agent named'} can claim this account. Ask them to sign in with that email and confirm here.</span>
        </div>
      </>
    );
  }
  return (
    <>
      {head}
      <section className="panel panel-pad stack-sm" aria-labelledby="claim-what">
        <h2 id="claim-what" className="h-sub">
          What the agent set up
        </h2>
        <p style={{ margin: 0 }}>
          <strong>{setUpBySentence(claim)}</strong>
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          It created {orgName}, a live and a test widget key for the website, and an AI agent token that acts for you. It accepted the
          Partner Terms for {orgName} on your authority.
        </p>
        <Facts claim={claim} />
      </section>
      <section className="panel panel-pad stack-sm" aria-labelledby="claim-confirm">
        <h2 id="claim-confirm" className="h-sub">
          Confirm
        </h2>
        <p style={{ margin: 0 }}>
          Read the <Link to={TERMS_PATH}>FinderlyFix Partner Terms</Link> (version of {legalVersionLabel(CURRENT_TERMS.terms_version)}).
        </p>
        <div className="check">
          <input id={checkboxId} type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
          <label htmlFor={checkboxId}>
            I have read the FinderlyFix Partner Terms and accept them for {orgName}, and I confirm I am authorised to bind it.
          </label>
        </div>
        <ErrorNotice error={confirm.error} />
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ticked || confirm.pending}
            onClick={async () => {
              const res = await confirm.run();
              if (res) {
                setDone({ reenabled: res.keys_reenabled });
                await refreshWhoami().catch(() => undefined);
              }
            }}
          >
            {confirm.pending ? 'Confirming…' : 'Confirm and take ownership'}
          </button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          Didn’t authorise this? Use the “This wasn’t us” link in the email we sent to {claim.contact_email ?? 'you'}.
        </p>
      </section>
    </>
  );
}
